import { BarChart } from '@mantine/charts';
import { Anchor, Badge, Breadcrumbs, Button, Card, Grid, Group, Loader, SegmentedControl, Table, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import LineChart from '../../components/Charts/LineChart';
import Metadata from '../../components/Metadata';
import MetricsSelector from '../../components/MetricsSelector';
import Spinner from '../../components/Spinner';
import { daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';

const categoryColor = { promoter: 'green', passive: 'yellow', detractor: 'red' };

const NpsScoreDisplay = ({ score }) => {
  if (score == null) return <Text c="dimmed" size="sm">No data</Text>;
  const color = score >= 50 ? 'green' : score >= 0 ? 'yellow' : 'red';
  return (
    <Text size={48} fw={800} c={color} lh={1}>
      {score > 0 ? `+${score}` : score}
    </Text>
  );
};

const NPSAnalyticsPage = () => {
  const fetch = useFetch();
  const { accountId, domainId, npsId } = useParams();

  const [npsSurvey, setNpsSurvey] = useState();
  const [scoreData, setScoreData] = useState();
  const [trendData, setTrendData] = useState();
  const [submissions, setSubmissions] = useState();
  const [verbatimGroup, setVerbatimGroup] = useState('all');
  const [verbatimPage, setVerbatimPage] = useState(1);
  const [granularity, setGranularity] = useState('weekly');
  const [surveyError, setSurveyError] = useState();
  const [error, setError] = useState();

  const [startDate, setStartDate] = useState(daysAgoISODate(29));
  const [endDate, setEndDate] = useState(todayISODate());

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/nps-surveys/${npsId}`)
      .then(({ npsSurvey }) => setNpsSurvey(npsSurvey))
      .catch(setSurveyError);
  }, [accountId, domainId, npsId, fetch]);

  useEffect(() => {
    setScoreData(undefined);
    setTrendData(undefined);

    const base = `/api/admin/accounts/${accountId}/domains/${domainId}/nps-surveys/${npsId}`;
    const dateParams = `startDate=${startDate}&endDate=${endDate}`;

    fetch(`${base}/score?${dateParams}`).then(setScoreData).catch(setError);
    fetch(`${base}/trend?${dateParams}&granularity=${granularity}`).then(setTrendData).catch(setError);
  }, [accountId, domainId, npsId, fetch, startDate, endDate, granularity]);

  useEffect(() => {
    setSubmissions(undefined);

    const base = `/api/admin/accounts/${accountId}/domains/${domainId}/nps-surveys/${npsId}`;
    const dateParams = `startDate=${startDate}&endDate=${endDate}`;
    const groupParam = verbatimGroup !== 'all' ? `&group=${verbatimGroup}` : '';
    fetch(`${base}/submissions?${dateParams}${groupParam}&page=${verbatimPage}`)
      .then(({ submissions }) => setSubmissions(submissions))
      .catch(setError);
  }, [accountId, domainId, npsId, fetch, startDate, endDate, verbatimGroup, verbatimPage]);

  if (surveyError) return <ErrorMessage message={surveyError.message} />;
  if (npsSurvey === undefined) return <Spinner />;
  if (!npsSurvey) return <ErrorMessage message="NPS survey not found." />;

  const distributionData = scoreData
    ? Array.from({ length: 11 }, (_, score) => {
        const cat = score >= 9 ? 'promoter' : score >= 7 ? 'passive' : 'detractor';
        const found = scoreData.distribution?.find((d) => d.score === score);
        return { score: String(score), count: found?.count || 0, category: cat };
      })
    : [];

  const trendChartData = trendData
    ? [
        {
          id: 'NPS Score',
          data: trendData.map((d) => ({ x: d.period.split('T')[0], y: d.score ?? null })),
        },
      ]
    : [];

  const distributionChartData = distributionData.map((d) => ({
    score: d.score,
    promoters: d.category === 'promoter' ? d.count : 0,
    passives: d.category === 'passive' ? d.count : 0,
    detractors: d.category === 'detractor' ? d.count : 0,
  }));

  return (
    <>
      <Metadata title={`NPS | ${npsSurvey.name}`} />
      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/nps`}>NPS Surveys</Anchor>
        <Text>{npsSurvey.name}</Text>
      </Breadcrumbs>

      <MetricsSelector
        startDate={startDate}
        endDate={endDate}
        setStartDate={(v) => { setStartDate(v); setVerbatimPage(1); }}
        setEndDate={(v) => { setEndDate(v); setVerbatimPage(1); }}
        rowSize="12"
      />

      {error && <ErrorMessage message={error.message} />}

      {/* Score + P/P/D breakdown */}
      <Grid mt="md">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Card withBorder ta="center" h="100%">
            <Title order={5} c="dimmed" mb="xs">NPS Score</Title>
            {scoreData ? <NpsScoreDisplay score={scoreData.score} /> : <Loader size="sm" />}
            {scoreData && <Text size="xs" c="dimmed" mt="xs">{scoreData.total} responses</Text>}
          </Card>
        </Grid.Col>
        {['promoters', 'passives', 'detractors'].map((group) => (
          <Grid.Col span={{ base: 12, sm: 3 }} key={group}>
            <Card withBorder ta="center" h="100%">
              <Title order={5} c={categoryColor[group.slice(0, -1)]} mb="xs" tt="capitalize">
                {group}
              </Title>
              {scoreData ? (
                <>
                  <Text size={32} fw={700} lh={1}>{scoreData[group]?.pct ?? 0}%</Text>
                  <Text size="xs" c="dimmed" mt="xs">{scoreData[group]?.count ?? 0} responses</Text>
                </>
              ) : (
                <Loader size="sm" />
              )}
            </Card>
          </Grid.Col>
        ))}
      </Grid>

      {/* Trend chart */}
      <Card withBorder mt="md">
        <Group justify="space-between" mb="sm">
          <Title order={5}>Score over time</Title>
          <SegmentedControl
            size="xs"
            value={granularity}
            onChange={setGranularity}
            data={[
              { value: 'weekly', label: 'Weekly' },
              { value: 'monthly', label: 'Monthly' },
            ]}
          />
        </Group>
        {trendData ? (
          <LineChart data={trendChartData} />
        ) : (
          <Loader size="sm" />
        )}
      </Card>

      {/* Distribution chart */}
      <Card withBorder mt="md">
        <Title order={5} mb="sm">Score distribution</Title>
        {scoreData ? (
          <BarChart
            h={200}
            data={distributionChartData}
            dataKey="score"
            series={[
              { name: 'promoters', color: 'green.6', label: 'Promoters' },
              { name: 'passives', color: 'yellow.6', label: 'Passives' },
              { name: 'detractors', color: 'red.6', label: 'Detractors' },
            ]}
            stackId="stack"
            xAxisLabel="Score"
          />
        ) : (
          <Loader size="sm" />
        )}
      </Card>

      {/* Verbatims */}
      <Card withBorder mt="md">
        <Group justify="space-between" mb="sm">
          <Title order={5}>Responses</Title>
          <SegmentedControl
            size="xs"
            value={verbatimGroup}
            onChange={(v) => { setVerbatimGroup(v); setVerbatimPage(1); }}
            data={[
              { value: 'all', label: 'All' },
              { value: 'promoter', label: 'Promoters' },
              { value: 'passive', label: 'Passives' },
              { value: 'detractor', label: 'Detractors' },
            ]}
          />
        </Group>

        {!submissions && <Loader size="sm" />}
        {submissions?.length === 0 && <Text c="dimmed" size="sm">No responses in this period.</Text>}
        {submissions?.length > 0 && (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>Score</Table.Th>
                  <Table.Th>Comment</Table.Th>
                  <Table.Th w={160}>Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {submissions.map((row, i) => (
                  <Table.Tr key={row.id ?? i}>
                    <Table.Td>
                      <Badge color={categoryColor[row.category] || 'gray'} variant="light">
                        {row.score}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{row.comment || <Text c="dimmed" size="xs">—</Text>}</Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {row.timestamp ? new Date(row.timestamp).toLocaleDateString() : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="center" mt="sm">
              <Button size="xs" variant="default" disabled={verbatimPage === 1} onClick={() => setVerbatimPage((p) => p - 1)}>
                Previous
              </Button>
              <Text size="sm">Page {verbatimPage}</Text>
              <Button size="xs" variant="default" onClick={() => setVerbatimPage((p) => p + 1)}>
                Next
              </Button>
            </Group>
          </>
        )}
      </Card>
    </>
  );
};

export default NPSAnalyticsPage;
